import { ReactNode } from "react";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import FolderSharedRoundedIcon from "@mui/icons-material/FolderSharedRounded";
import MedicalServicesRoundedIcon from "@mui/icons-material/MedicalServicesRounded";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import React from "react";

export interface MenuItem {
  title: string;
  path: string;
  icon: ReactNode;
  requiresSuperAdmin?: boolean;
}

export interface MenuCategory {
  category: string;
  icon: ReactNode;
  items: MenuItem[];
}

export const menuCategories: MenuCategory[] = [
  {
    category: "Dashboard",
    icon: React.createElement(DashboardRoundedIcon, { fontSize: "small" }),
    items: [
      {
        title: "Overview",
        path: "/dashboard",
        icon: React.createElement(DashboardRoundedIcon, { fontSize: "small" }),
      },
    ],
  },
  {
    category: "User Management",
    icon: React.createElement(PeopleAltRoundedIcon, { fontSize: "small" }),
    items: [
      {
        title: "Clients",
        path: "/patients",
        icon: React.createElement(PersonRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Clinicians",
        path: "/therapists",
        icon: React.createElement(LocalHospitalRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Clinician Approval",
        path: "/clinician-approval",
        icon: React.createElement(HowToRegRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Specialist Registrations",
        path: "/specialist-registrations",
        icon: React.createElement(SchoolRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Admin Users",
        icon: React.createElement(AdminPanelSettingsRoundedIcon, { fontSize: "small" }),
        path: "/users",
        requiresSuperAdmin: true,
      },
    ],
  },
  {
    category: "Sessions & Records",
    icon: React.createElement(EventNoteRoundedIcon, { fontSize: "small" }),
    items: [
      {
        title: "Sessions",
        path: "/sessions",
        icon: React.createElement(EventNoteRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Medical Records",
        path: "/medical-records",
        icon: React.createElement(FolderSharedRoundedIcon, { fontSize: "small" }),
      },
    ],
  },
  {
    category: "Services & Configuration",
    icon: React.createElement(MedicalServicesRoundedIcon, { fontSize: "small" }),
    items: [
      {
        title: "Services",
        path: "/services",
        icon: React.createElement(MedicalServicesRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Symptoms",
        path: "/symptoms",
        icon: React.createElement(MonitorHeartRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Pricing",
        path: "/pricing",
        icon: React.createElement(AttachMoneyRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Resources",
        path: "/resources",
        icon: React.createElement(MenuBookRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Blog",
        path: "/blog",
        icon: React.createElement(ArticleRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "AI Blog Generator",
        path: "/blog/generator",
        icon: React.createElement(AutoAwesomeRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "App Configuration",
        path: "/app-config",
        icon: React.createElement(TuneRoundedIcon, { fontSize: "small" }),
      },
    ],
  },
  {
    category: "Marketing",
    icon: React.createElement(CampaignRoundedIcon, { fontSize: "small" }),
    items: [
      {
        title: "Coupons",
        path: "/coupons",
        icon: React.createElement(LocalOfferRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Social Dashboard",
        path: "/social-media",
        icon: React.createElement(ShareRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Campaigns",
        path: "/social-media/campaigns",
        icon: React.createElement(CampaignRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Platforms",
        path: "/social-media/platforms",
        icon: React.createElement(ShareRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Content Calendar",
        path: "/social-media/calendar",
        icon: React.createElement(CalendarMonthRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Social Analytics",
        path: "/social-media/analytics",
        icon: React.createElement(InsightsRoundedIcon, { fontSize: "small" }),
      },
    ],
  },
  {
    category: "Financial",
    icon: React.createElement(AccountBalanceWalletRoundedIcon, { fontSize: "small" }),
    items: [
      {
        title: "Payouts",
        path: "/payouts",
        icon: React.createElement(AccountBalanceWalletRoundedIcon, { fontSize: "small" }),
      },
    ],
  },
  {
    category: "System",
    icon: React.createElement(SettingsRoundedIcon, { fontSize: "small" }),
    items: [
      {
        title: "Reports",
        path: "/reports",
        icon: React.createElement(AssessmentRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Notifications",
        path: "/notifications-management",
        icon: React.createElement(NotificationsRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Analytics",
        path: "/analytics",
        icon: React.createElement(InsightsRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "User Analytics",
        path: "/user-analytics",
        icon: React.createElement(QueryStatsRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "Translations",
        path: "/translations",
        icon: React.createElement(TranslateRoundedIcon, { fontSize: "small" }),
      },
      {
        title: "System Settings",
        path: "/system-settings",
        icon: React.createElement(SettingsRoundedIcon, { fontSize: "small" }),
      },
    ],
  },
  {
    category: "Support",
    icon: React.createElement(SupportAgentRoundedIcon, { fontSize: "small" }),
    items: [
      {
        title: "Support Tickets",
        path: "/tickets",
        icon: React.createElement(SupportAgentRoundedIcon, { fontSize: "small" }),
      },
    ],
  },
];

// Legacy export for backward compatibility
export const menuItems = menuCategories.flatMap((category) => category.items);

export const headerTitle = [
  {
    title: "Dashboard",
    path: "/dashboard",
  },
  {
    title: "Incentive Programs",
    path: "/incentive-programs",
  },
  {
    title: "Payouts",
    path: "/payouts",
  },
  {
    title: "Employees",
    path: "/employees",
  },
  {
    title: "Disputes",
    path: "/disputes",
  },
  {
    title: "Reports",
    path: "/reports",
  },
  {
    title: "Master Data",
    path: "/master-data",
  },
  {
    title: "Source Data",
    path: "/source-data",
  },
];
